import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import {
  CreateFinancialAccountPayload,
  FinanceApiService,
  FinancialAccountRecord,
  FinancialBook,
  UpdateFinancialAccountPayload
} from './finance-api.service';

interface AccountTreeRow {
  account: FinancialAccountRecord;
  level: number;
}

type AccountType = 'Asset' | 'Capital' | 'Liability' | 'ProfitAndLoss' | 'Regulatory';
type AccountNature = 'Debtor' | 'Creditor' | 'NotImportant';
type FloatingEntityType = 'None' | 'Person' | 'OrganizationUnit' | 'Bank' | 'CashFund' | 'Warehouse';

@Component({
  standalone: true,
  selector: 'ain-finance-chart-of-accounts',
  imports: [AinPageComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('finance.chart.title')" [description]="t('finance.chart.description')" (click)="closeRowMenu()">
      <div class="toolbar">
        <label>
          <span>{{ t('finance.chart.fields.book') }}</span>
          <p-select
            class="field-select"
            [options]="books()"
            optionValue="id"
            [filter]="true"
            filterBy="code,name"
            [ngModel]="selectedBookId"
            (ngModelChange)="selectedBookId = $event; onBookChanged()"
            appendTo="body">
            <ng-template pTemplate="selectedItem" let-book>
              {{ book ? (book.code + ' - ' + book.name) : '-' }}
            </ng-template>
            <ng-template pTemplate="item" let-book>
              {{ book.code }} - {{ book.name }}
            </ng-template>
          </p-select>
        </label>
        <div class="actions">
          <button pButton type="button" [label]="t('finance.chart.refresh')" severity="secondary" (click)="loadAccounts()"></button>
          <button pButton type="button" [label]="t('finance.chart.addRoot')" (click)="startCreateRoot()"></button>
          <button pButton type="button" [label]="t('finance.chart.addChild')" severity="secondary" [disabled]="!canAddChild()" (click)="startCreateChild()"></button>
          <button pButton type="button" [label]="t('finance.chart.editSelected')" severity="secondary" [disabled]="!selectedAccount()" (click)="startEdit()"></button>
        </div>
      </div>

      <p-table [value]="rows()" selectionMode="single" [selection]="selectedAccount()" (selectionChange)="onSelect($event)" dataKey="id">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('finance.chart.fields.account') }}</th>
            <th>{{ t('finance.chart.fields.level') }}</th>
            <th>{{ t('finance.chart.fields.type') }}</th>
            <th>{{ t('finance.chart.fields.nature') }}</th>
            <th>{{ t('finance.chart.fields.floatingEntityType') }}</th>
            <th>{{ t('finance.chart.fields.status') }}</th>
            <th>{{ t('finance.chart.fields.isPostingAllowed') }}</th>
            <th>{{ t('finance.chart.fields.isActive') }}</th>
            <th>{{ t('finance.chart.fields.actions') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr [pSelectableRow]="row.account">
            <td>
              <div class="account-cell" [style.padding-inline-start.px]="row.level * 18">
                <button
                  type="button"
                  class="expand-btn"
                  [class.placeholder]="!hasChildren(row.account.id)"
                  [disabled]="!hasChildren(row.account.id)"
                  (click)="toggleExpand(row.account.id, $event)">
                  {{ isExpanded(row.account.id) ? '▾' : '▸' }}
                </button>
                <span class="code">{{ row.account.code }}</span>
                <span class="name">{{ row.account.name }}</span>
              </div>
            </td>
            <td>{{ accountLevelLabel(row.account.level) }}</td>
            <td>{{ accountTypeLabel(row.account.accountType) }}</td>
            <td>{{ accountNatureLabel(row.account.nature) }}</td>
            <td>{{ floatingEntityTypeLabel(row.account.floatingEntityType) }}</td>
            <td>{{ accountStatusLabel(row.account.status) }}</td>
            <td>{{ row.account.isPostingAllowed ? t('common.yes') : t('common.no') }}</td>
            <td>{{ row.account.isActive ? t('payments.common.enabled') : t('payments.common.disabled') }}</td>
            <td class="actions-cell">
              <button
                pButton
                type="button"
                size="small"
                icon="pi pi-ellipsis-h"
                severity="secondary"
                text="true"
                class="menu-trigger"
                (click)="toggleRowMenu(row.account.id, $event)">
              </button>
              @if (openRowMenuId() === row.account.id) {
                <div class="row-menu" (click)="$event.stopPropagation()">
                  <button type="button" (click)="startCreateChildFromRow(row.account)">{{ t('finance.chart.menu.newChild') }}</button>
                  <button type="button" (click)="startEditFromRow(row.account)">{{ t('common.edit') }}</button>
                  <button type="button" class="danger" (click)="deleteAccount(row.account)">{{ t('common.delete') }}</button>
                </div>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }

      @if (dialogOpen()) {
        <div class="modal-backdrop" (click)="cancelDialog()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <h3>{{ mode() === 'edit' ? t('finance.chart.form.editTitle') : t('finance.chart.form.createTitle') }}</h3>
            <div class="form">
              <label>
                <span>{{ t('finance.chart.fields.code') }}</span>
                <input pInputText type="text" [(ngModel)]="form.code" />
              </label>
              <label>
                <span>{{ t('finance.chart.fields.name') }}</span>
                <input pInputText type="text" [(ngModel)]="form.name" />
              </label>
              <label>
                <span>{{ t('finance.chart.fields.parent') }}</span>
                <p-select
                  class="field-select"
                  [options]="parentAccountOptions()"
                  optionLabel="label"
                  optionValue="id"
                  [filter]="true"
                  filterBy="label"
                  [(ngModel)]="form.parentAccountId"
                  (ngModelChange)="applyDerivedFields()"
                  appendTo="self">
                </p-select>
              </label>
              <label>
                <span>{{ t('finance.chart.fields.accountType') }}</span>
                <p-select
                  class="field-select"
                  [options]="accountTypeOptions"
                  optionValue="value"
                  [(ngModel)]="form.accountType"
                  [disabled]="isAccountTypeLocked()"
                  (ngModelChange)="applyDerivedFields()"
                  appendTo="self">
                  <ng-template pTemplate="selectedItem" let-item>
                    {{ item ? t(item.labelKey) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-item>
                    {{ t(item.labelKey) }}
                  </ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('finance.chart.fields.nature') }}</span>
                <p-select
                  class="field-select"
                  [options]="availableNatureOptions()"
                  optionValue="value"
                  [(ngModel)]="form.nature"
                  appendTo="self">
                  <ng-template pTemplate="selectedItem" let-item>
                    {{ item ? t(item.labelKey) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-item>
                    {{ t(item.labelKey) }}
                  </ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('finance.chart.fields.status') }}</span>
                <input pInputText type="text" [ngModel]="accountStatusLabel(form.status)" disabled />
              </label>
              @if (showFloatingEntityType()) {
                <label>
                  <span>{{ t('finance.chart.fields.floatingEntityType') }}</span>
                  <p-select
                    class="field-select"
                    [options]="floatingEntityTypeOptions"
                    optionValue="value"
                    [(ngModel)]="form.floatingEntityType"
                    appendTo="self">
                    <ng-template pTemplate="selectedItem" let-item>
                      {{ item ? t(item.labelKey) : '-' }}
                    </ng-template>
                    <ng-template pTemplate="item" let-item>
                      {{ t(item.labelKey) }}
                    </ng-template>
                  </p-select>
                </label>
              }
              <label class="check">
                <input type="checkbox" [(ngModel)]="form.isPostingAllowed" />
                <span>{{ t('finance.chart.fields.isPostingAllowed') }}</span>
              </label>
              <label class="check">
                <input type="checkbox" [(ngModel)]="form.isActive" />
                <span>{{ t('finance.chart.fields.isActive') }}</span>
              </label>
              <label>
                <span>{{ t('finance.chart.fields.level') }}</span>
                <input pInputText type="text" [ngModel]="accountLevelLabel(form.level)" disabled />
              </label>
            </div>
            <div class="actions">
              <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
              <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="cancelDialog()"></button>
            </div>
          </div>
        </div>
      }

      @if (deleteDialogOpen()) {
        <div class="modal-backdrop" (click)="closeDeleteDialog()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <h3>{{ t('common.delete') }}</h3>
            <p>{{ t('finance.chart.messages.deleteConfirm') }}</p>
            <div class="actions">
              <button pButton type="button" severity="danger" [label]="t('common.delete')" (click)="confirmDeleteAccount()"></button>
              <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="closeDeleteDialog()"></button>
            </div>
          </div>
        </div>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
    label { display: grid; gap: 6px; }
    .field-select { width: 100%; }
    input[type='text'] { height: 38px; border: 1px solid #d1d5db; border-radius: 8px; padding: 0 10px; }
    .actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: end; }
    .account-cell { display: flex; gap: 8px; align-items: center; }
    .expand-btn { border: 0; background: transparent; color: #334155; width: 18px; cursor: pointer; padding: 0; }
    .expand-btn.placeholder { visibility: hidden; }
    .code { font-weight: 700; min-width: 80px; }
    .name { color: #0f172a; }
    .actions-cell { white-space: nowrap; position: relative; width: 60px; text-align: center; overflow: visible; }
    .menu-trigger { padding: 0 !important; width: 1.75rem; height: 1.75rem; }
    .row-menu {
      position: absolute;
      inset-inline-end: 8px;
      top: calc(100% - 4px);
      z-index: 1400;
      min-width: 140px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #fff;
      box-shadow: 0 10px 24px rgb(15 23 42 / 16%);
      display: grid;
      padding: 4px;
    }
    .row-menu button {
      border: 0;
      background: transparent;
      text-align: start;
      padding: 8px;
      border-radius: 6px;
      cursor: pointer;
      color: #0f172a;
    }
    .row-menu button:hover { background: #f1f5f9; }
    .row-menu button.danger { color: #b91c1c; }
    .row-menu button.danger:hover { background: #fee2e2; }
    .message { margin: 8px 0 0; font-size: 13px; }
    .error { color: #b91c1c; }
    .success { color: #15803d; }
    .modal-backdrop { position: fixed; inset: 0; background: rgb(15 23 42 / 50%); display: grid; place-items: center; z-index: 1200; padding: 16px; }
    .modal-card { width: min(560px, 100%); max-height: calc(100vh - 32px); overflow: auto; background: white; border-radius: 12px; border: 1px solid #d1d5db; padding: 14px; display: grid; gap: 10px; }
    :host ::ng-deep .modal-card .p-select-overlay { z-index: 1601 !important; }
    :host ::ng-deep .modal-card .p-overlay { z-index: 1601 !important; }
    :host ::ng-deep .p-datatable-wrapper { overflow: visible !important; }
    :host ::ng-deep .p-datatable-table-container { overflow: visible !important; }
    :host ::ng-deep .p-datatable-table { overflow: visible !important; }
    .form { display: grid; gap: 10px; }
    .check { display: flex; align-items: center; gap: 8px; }
  `]
})
export class FinanceChartOfAccountsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly books = signal<FinancialBook[]>([]);
  readonly allAccounts = signal<FinancialAccountRecord[]>([]);
  readonly rows = signal<AccountTreeRow[]>([]);
  readonly parentRows = signal<AccountTreeRow[]>([]);
  readonly selectedAccount = signal<FinancialAccountRecord | null>(null);
  readonly dialogOpen = signal(false);
  readonly deleteDialogOpen = signal(false);
  readonly mode = signal<'create' | 'edit'>('create');
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly openRowMenuId = signal<string | null>(null);
  readonly expandedById = signal<Record<string, boolean>>({});
  readonly childCountById = signal<Record<string, number>>({});

  selectedBookId = '';
  private pendingDeleteAccount: FinancialAccountRecord | null = null;
  form = {
    code: '',
    name: '',
    parentAccountId: '',
    accountType: 'Asset' as AccountType,
    nature: 'Debtor' as AccountNature,
    status: 'BalanceSheet',
    level: 'Group',
    floatingEntityType: 'None' as FloatingEntityType,
    isPostingAllowed: false,
    isActive: true
  };

  readonly accountTypeOptions: { value: AccountType; labelKey: string }[] = [
    { value: 'Asset', labelKey: 'finance.chart.accountTypes.asset' },
    { value: 'Capital', labelKey: 'finance.chart.accountTypes.capital' },
    { value: 'Liability', labelKey: 'finance.chart.accountTypes.liability' },
    { value: 'ProfitAndLoss', labelKey: 'finance.chart.accountTypes.profitAndLoss' },
    { value: 'Regulatory', labelKey: 'finance.chart.accountTypes.regulatory' }
  ];

  readonly floatingEntityTypeOptions: { value: FloatingEntityType; labelKey: string }[] = [
    { value: 'None', labelKey: 'finance.chart.floatingEntity.none' },
    { value: 'Person', labelKey: 'finance.chart.floatingEntity.person' },
    { value: 'OrganizationUnit', labelKey: 'finance.chart.floatingEntity.organizationUnit' },
    { value: 'Bank', labelKey: 'finance.chart.floatingEntity.bank' },
    { value: 'CashFund', labelKey: 'finance.chart.floatingEntity.cashFund' },
    { value: 'Warehouse', labelKey: 'finance.chart.floatingEntity.warehouse' }
  ];

  ngOnInit(): void {
    this.api.listBooks().subscribe({
      next: books => {
        this.books.set(books);
        this.selectedBookId = books[0]?.id ?? '';
        this.loadAccounts();
      },
      error: () => this.errorMessage.set(this.t('finance.chart.messages.loadBooksFailed'))
    });
  }

  onBookChanged(): void {
    this.selectedAccount.set(null);
    this.loadAccounts();
  }

  loadAccounts(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.listFinancialAccounts(this.selectedBookId || undefined).subscribe({
      next: accounts => {
        this.allAccounts.set(accounts);
        this.rebuildRows(accounts);
        this.closeRowMenu();
      },
      error: () => this.errorMessage.set(this.t('finance.chart.messages.loadFailed'))
    });
  }

  onSelect(account: FinancialAccountRecord | null): void {
    this.selectedAccount.set(account);
    this.closeRowMenu();
  }

  startCreateRoot(): void {
    this.mode.set('create');
    this.form = this.createDefaultForm();
    this.parentRows.set(this.rows());
    this.closeRowMenu();
    this.dialogOpen.set(true);
  }

  startCreateChild(): void {
    const selected = this.selectedAccount();
    if (!selected) {
      return;
    }

    this.mode.set('create');
    const nextLevel = this.nextLevelFromParent(selected.level);
    this.form = {
      code: '',
      name: '',
      parentAccountId: selected.id,
      accountType: selected.accountType as AccountType,
      nature: selected.nature as AccountNature,
      status: selected.status,
      level: nextLevel,
      floatingEntityType: 'None',
      isPostingAllowed: true,
      isActive: true
    };
    this.parentRows.set(this.rows());
    this.applyDerivedFields();
    this.closeRowMenu();
    this.dialogOpen.set(true);
  }

  startEdit(): void {
    const selected = this.selectedAccount();
    if (!selected) {
      this.errorMessage.set(this.t('finance.chart.messages.noSelection'));
      return;
    }

    this.mode.set('edit');
    this.form = {
      code: selected.code,
      name: selected.name,
      parentAccountId: selected.parentAccountId ?? '',
      accountType: (selected.accountType as AccountType) || 'Asset',
      nature: (selected.nature as AccountNature) || 'Debtor',
      status: selected.status || 'BalanceSheet',
      level: selected.level || 'Group',
      floatingEntityType: (selected.floatingEntityType as FloatingEntityType) || 'None',
      isPostingAllowed: selected.isPostingAllowed,
      isActive: selected.isActive
    };
    this.parentRows.set(this.rows().filter(item => item.account.id !== selected.id));
    this.closeRowMenu();
    this.dialogOpen.set(true);
  }

  save(): void {
    const code = this.form.code.trim();
    const name = this.form.name.trim();
    if (!this.selectedBookId || !code || !name) {
      this.errorMessage.set(this.t('finance.chart.messages.invalidForm'));
      return;
    }

    this.applyDerivedFields();
    this.errorMessage.set('');
    this.successMessage.set('');
    const selected = this.selectedAccount();
    if (this.mode() === 'edit' && selected) {
      const payload: UpdateFinancialAccountPayload = {
        legalEntityId: selected.legalEntityId,
        code,
        name,
        parentAccountId: this.normalizeGuid(this.form.parentAccountId),
        accountType: this.form.accountType,
        nature: this.form.nature,
        floatingEntityType: this.showFloatingEntityType() ? this.form.floatingEntityType : 'None',
        isPostingAllowed: this.form.isPostingAllowed,
        isActive: this.form.isActive
      };
      this.api.updateFinancialAccount(selected.id, payload).subscribe({
        next: () => {
          this.successMessage.set(this.t('finance.chart.messages.saved'));
          this.dialogOpen.set(false);
          this.loadAccounts();
        },
        error: () => this.errorMessage.set(this.t('finance.chart.messages.saveFailed'))
      });
      return;
    }

    const payload: CreateFinancialAccountPayload = {
      bookId: this.selectedBookId,
      legalEntityId: this.books().find(item => item.id === this.selectedBookId)?.legalEntityId,
      code,
      name,
      parentAccountId: this.normalizeGuid(this.form.parentAccountId),
      accountType: this.form.accountType,
      nature: this.form.nature,
      floatingEntityType: this.showFloatingEntityType() ? this.form.floatingEntityType : 'None',
      isPostingAllowed: this.form.isPostingAllowed
    };
    this.api.createFinancialAccount(payload).subscribe({
      next: () => {
        this.successMessage.set(this.t('finance.chart.messages.created'));
        this.dialogOpen.set(false);
        this.loadAccounts();
      },
      error: () => this.errorMessage.set(this.t('finance.chart.messages.saveFailed'))
    });
  }

  cancelDialog(): void {
    this.dialogOpen.set(false);
  }

  startEditFromRow(account: FinancialAccountRecord): void {
    this.selectedAccount.set(account);
    this.closeRowMenu();
    this.startEdit();
  }

  canAddChild(): boolean {
    return !!this.selectedAccount() && this.selectedAccount()!.level !== 'Floating';
  }

  startCreateChildFromRow(account: FinancialAccountRecord): void {
    this.selectedAccount.set(account);
    this.closeRowMenu();
    this.startCreateChild();
  }

  deleteAccount(account: FinancialAccountRecord): void {
    this.closeRowMenu();
    this.pendingDeleteAccount = account;
    this.deleteDialogOpen.set(true);
  }

  closeDeleteDialog(): void {
    this.deleteDialogOpen.set(false);
    this.pendingDeleteAccount = null;
  }

  confirmDeleteAccount(): void {
    const account = this.pendingDeleteAccount;
    if (!account) return;

    this.api.deleteFinancialAccount(account.id).subscribe({
      next: () => {
        this.successMessage.set(this.t('finance.chart.messages.deleted'));
        if (this.selectedAccount()?.id === account.id) {
          this.selectedAccount.set(null);
        }
        this.closeDeleteDialog();
        this.loadAccounts();
      },
      error: () => this.errorMessage.set(this.t('finance.chart.messages.deleteFailed'))
    });
  }

  parentLabel(row: AccountTreeRow): string {
    return `${'  '.repeat(row.level)}${row.account.code} - ${row.account.name}`;
  }

  parentAccountOptions(): { id: string; label: string }[] {
    return [
      { id: '', label: this.t('finance.chart.rootAccount') },
      ...this.parentRows().map(row => ({ id: row.account.id, label: this.parentLabel(row) }))
    ];
  }

  isAccountTypeLocked(): boolean {
    const parent = this.getSelectedParent();
    return !!parent;
  }

  availableNatureOptions(): { value: AccountNature; labelKey: string }[] {
    switch (this.form.accountType) {
      case 'Asset':
        return [{ value: 'Debtor', labelKey: 'finance.chart.natures.debtor' }];
      case 'Capital':
      case 'Liability':
        return [{ value: 'Creditor', labelKey: 'finance.chart.natures.creditor' }];
      default:
        return [{ value: 'NotImportant', labelKey: 'finance.chart.natures.notImportant' }];
    }
  }

  showFloatingEntityType(): boolean {
    return this.form.level !== 'Group' && this.form.level !== 'General';
  }

  accountTypeLabel(value?: string): string {
    if (!value) return '-';
    return this.t(`finance.chart.accountTypes.${this.toCamelCase(value)}`);
  }

  accountNatureLabel(value?: string): string {
    if (!value) return '-';
    return this.t(`finance.chart.natures.${this.toCamelCase(value)}`);
  }

  accountStatusLabel(value?: string): string {
    if (!value) return '-';
    return this.t(`finance.chart.statuses.${this.toCamelCase(value)}`);
  }

  accountLevelLabel(value?: string): string {
    if (!value) return '-';
    return this.t(`finance.chart.levels.${this.toCamelCase(value)}`);
  }

  floatingEntityTypeLabel(value?: string): string {
    if (!value) return '-';
    return this.t(`finance.chart.floatingEntity.${this.toCamelCase(value)}`);
  }

  private toCamelCase(value: string): string {
    return value.length === 0 ? value : value.charAt(0).toLowerCase() + value.slice(1);
  }

  private createDefaultForm() {
    return {
      code: '',
      name: '',
      parentAccountId: '',
      accountType: 'Asset' as AccountType,
      nature: 'Debtor' as AccountNature,
      status: 'BalanceSheet',
      level: 'Group',
      floatingEntityType: 'None' as FloatingEntityType,
      isPostingAllowed: false,
      isActive: true
    };
  }

  private getSelectedParent(): FinancialAccountRecord | undefined {
    const parentId = this.normalizeGuid(this.form.parentAccountId);
    if (!parentId) {
      return undefined;
    }
    return this.allAccounts().find(item => item.id === parentId);
  }

  applyDerivedFields(): void {
    const parent = this.getSelectedParent();
    if (parent) {
      this.form.accountType = parent.accountType as AccountType;
      this.form.level = this.nextLevelFromParent(parent.level);
    } else {
      this.form.level = 'Group';
    }

    if (this.form.accountType === 'Asset') {
      this.form.nature = 'Debtor';
      this.form.status = 'BalanceSheet';
    } else if (this.form.accountType === 'Capital' || this.form.accountType === 'Liability') {
      this.form.nature = 'Creditor';
      this.form.status = 'BalanceSheet';
    } else if (this.form.accountType === 'ProfitAndLoss') {
      this.form.nature = 'NotImportant';
      this.form.status = 'ProfitAndLoss';
    } else {
      this.form.nature = 'NotImportant';
      this.form.status = 'BalanceSheet';
    }

    if (!this.showFloatingEntityType()) {
      this.form.floatingEntityType = 'None';
    }
  }

  private nextLevelFromParent(parentLevel: string): string {
    if (parentLevel === 'Group') return 'General';
    if (parentLevel === 'General') return 'Subsidiary';
    if (parentLevel === 'Subsidiary') return 'Detail';
    return 'Floating';
  }

  private normalizeGuid(value: string): string | undefined {
    const normalized = value.trim();
    return normalized ? normalized : undefined;
  }

  private rebuildRows(accounts: FinancialAccountRecord[]): void {
    const byParent = new Map<string, FinancialAccountRecord[]>();
    const ids = new Set(accounts.map(item => item.id));
    for (const account of accounts) {
      const parentKey = account.parentAccountId && ids.has(account.parentAccountId)
        ? account.parentAccountId
        : 'root';
      if (!byParent.has(parentKey)) {
        byParent.set(parentKey, []);
      }
      byParent.get(parentKey)!.push(account);
    }

    for (const [, children] of byParent) {
      children.sort((a, b) => a.code.localeCompare(b.code));
    }

    const childCounts: Record<string, number> = {};
    for (const [parent, children] of byParent.entries()) {
      if (parent !== 'root') {
        childCounts[parent] = children.length;
      }
    }
    this.childCountById.set(childCounts);

    const fullRows: AccountTreeRow[] = [];
    const walk = (parentId: string, level: number): void => {
      const children = byParent.get(parentId) ?? [];
      for (const child of children) {
        fullRows.push({ account: child, level });
        walk(child.id, level + 1);
      }
    };
    walk('root', 0);

    const previousExpanded = this.expandedById();
    const nextExpanded: Record<string, boolean> = {};
    for (const row of fullRows) {
      if (row.level === 0) {
        nextExpanded[row.account.id] = previousExpanded[row.account.id] ?? true;
      } else {
        nextExpanded[row.account.id] = previousExpanded[row.account.id] ?? false;
      }
    }
    this.expandedById.set(nextExpanded);

    const visibleRows: AccountTreeRow[] = [];
    const rowById = new Map(fullRows.map(row => [row.account.id, row]));
    for (const row of fullRows) {
      let parentId = row.account.parentAccountId;
      let visible = true;
      while (parentId) {
        const parentRow = rowById.get(parentId);
        if (!parentRow) {
          break;
        }
        if (!this.expandedById()[parentId]) {
          visible = false;
          break;
        }
        parentId = parentRow.account.parentAccountId;
      }
      if (visible) {
        visibleRows.push(row);
      }
    }

    this.rows.set(visibleRows);
    this.parentRows.set(fullRows);
  }

  toggleExpand(accountId: string, event: Event): void {
    event.stopPropagation();
    if (!this.hasChildren(accountId)) {
      return;
    }
    const current = this.expandedById();
    this.expandedById.set({
      ...current,
      [accountId]: !current[accountId]
    });
    this.rebuildRows(this.allAccounts());
  }

  hasChildren(accountId: string): boolean {
    return (this.childCountById()[accountId] ?? 0) > 0;
  }

  isExpanded(accountId: string): boolean {
    return !!this.expandedById()[accountId];
  }

  toggleRowMenu(accountId: string, event: Event): void {
    event.stopPropagation();
    this.openRowMenuId.set(this.openRowMenuId() === accountId ? null : accountId);
  }

  closeRowMenu(): void {
    this.openRowMenuId.set(null);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
